import { useEffect, useState, useRef } from "react";

// Utility for caching
const LOCAL_STORAGE_KEY = "cached_user_location";
const MAX_FAILURES = 3;
const RETRY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

function useUserLocation() {
  const [exactLocation, setLocation] = useState(null);
  const [address, setAddress] = useState(null);
  const [error, setError] = useState(null);

  // Track failures and block time
  const failureCount = useRef(0);
  const blockUntil = useRef(0);
  const intervalId = useRef(null);

  const fetchLocation = async () => {
    // Throttle if too many failures
    if (
      failureCount.current >= MAX_FAILURES &&
      Date.now() < blockUntil.current
    ) {
      setError("Too many failures. Retrying later.");
      return;
    }

    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });

        try {
          // 1. Try OpenStreetMap
          const osmResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );

          if (!osmResponse.ok) {
            throw new Error("OpenStreetMap failed");
          }

          const osmData = await osmResponse.json();
          const { city, town, village, state, country, state_district } =
            osmData.address || {};

          const addressData = {
            city: city || town || village || state_district || "Ahmedabad",
            state: state || "Gujarat",
            country: country || "India",
          };

          setAddress(addressData);

          // Cache result
          localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({
              exactLocation: { latitude, longitude },
              address: addressData,
            })
          );
          failureCount.current = 0; // Reset on success
        } catch (err) {
          // 2. Fallback: use Geoapify
          try {
            const GEO_API_FY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;
            const fallbackRes = await fetch(
              `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&apiKey=${GEO_API_FY_API_KEY}`
            );

            if (!fallbackRes.ok) throw new Error("Fallback failed");

            const fallbackData = await fallbackRes.json();
            const locationInfo = fallbackData.features?.[0]?.properties || {};

            const fallbackAddress = {
              city: locationInfo.city || "Ahmedabad",
              state: locationInfo.state || "Gujarat",
              country: locationInfo.country || "India",
            };

            setAddress(fallbackAddress);

            localStorage.setItem(
              LOCAL_STORAGE_KEY,
              JSON.stringify({
                exactLocation: { latitude, longitude },
                address: fallbackAddress,
              })
            );
            failureCount.current = 0; // Reset on success
          } catch (fallbackErr) {
            failureCount.current += 1;
            if (failureCount.current >= MAX_FAILURES) {
              blockUntil.current = Date.now() + RETRY_TIMEOUT;
            }
            setError("Failed to fetch address info");
          }
        }
      },
      (geoErr) => {
        failureCount.current += 1;
        if (failureCount.current >= MAX_FAILURES) {
          blockUntil.current = Date.now() + RETRY_TIMEOUT;
        }

        // If user denies location permission, mark it in sessionStorage
        if (geoErr.code === 1) {
          // PERMISSION_DENIED
          // console.log("geoErr", "user Denied location");
          localStorage.setItem("userLocationSet", "denied");
        }

        setError(geoErr.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    const cachedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      setLocation(parsed.exactLocation);
      setAddress(parsed.address);
    }

    // ✅ Check if user already denied location previously
    const userLocationSet = localStorage.getItem("userLocationSet");
    if (userLocationSet === "denied" || userLocationSet === "false") {
      // console.log(
      //   "Skipping location fetch because user denied permission previously."
      // );
      return;
    } else {
      fetchLocation(); // Initial fetch if not denied
    }

    // Set up interval for refreshing location
    intervalId.current = setInterval(fetchLocation, REFRESH_INTERVAL);

    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { exactLocation, address, error };
}

export default useUserLocation;
